import clone from 'clone';
import PropTypes from 'prop-types';

export const OEFNStartSelectionItemTypes = {
    module:                 'module',
    category:               'category',
    subcategory:            'subcategory',
    item:                   'item'
};

//
export const OEFNStartSelectionContentFactory = {};

OEFNStartSelectionContentFactory.isModule = function(item) {
    return typeof(item) === 'object' && (item.type === OEFNStartSelectionItemTypes.module || Array.isArray(item.categories));
};

OEFNStartSelectionContentFactory.isCategory = function(item) {
    return typeof(item) === 'object' && (item.type === OEFNStartSelectionItemTypes.category || Array.isArray(item.subcategories));
};

OEFNStartSelectionContentFactory.isModuleOrCategory = function(item) {
    return typeof(item) === 'object' && (item.type === OEFNStartSelectionItemTypes.module || Array.isArray(item.categories) || item.type === OEFNStartSelectionItemTypes.category || Array.isArray(item.subcategories));
};

OEFNStartSelectionContentFactory.normalizeContent = function(content)    {
    if(Array.isArray(content))  return content;
    if(this.isModuleOrCategory(content))    return [content];
    return [];
}

OEFNStartSelectionContentFactory.isContent = function(content)  {
    return Array.isArray(content) || this.isModuleOrCategory(content);
}

OEFNStartSelectionContentFactory.areCategories = function(content) {
    content = this.normalizeContent(content);

    if(!Array.isArray(content) || !content.length)  return true;

    for(let i = 0; i < content.length; ++i) {
        let item = content[i];
        if(item.type === OEFNStartSelectionItemTypes.module || Array.isArray(item.categories))    return false;
        if(item.type === OEFNStartSelectionItemTypes.category || Array.isArray(item.subcategories))    return true;
    }

    return true;
};

OEFNStartSelectionContentFactory.areModulesHidden = function(content) {
    content = this.normalizeContent(content);

    if(OEFNStartSelectionContentFactory.areCategories(content))    return true;

    for(let i = 0; i < content.length; ++i) {
        let module = content[i];
        if(!module.isHidden)    return false;
    }

    return true;
};

OEFNStartSelectionContentFactory.itemCount = function(content) {
    content = this.normalizeContent(content);

    if(!Array.isArray(content) || !content.length)  return 0;

    let count = 0;
    if(OEFNStartSelectionContentFactory.areCategories(content))    {
        content.forEach(cat => { if(Array.isArray(cat.subcategories)) cat.subcategories.forEach(subcat => { if(Array.isArray(subcat.items)) count += subcat.items.length; }); });
    } else {
        content.forEach(module => { if(Array.isArray(module.categories)) module.categories.forEach(cat => { if(Array.isArray(cat.subcategories)) cat.subcategories.forEach(subcat => { if(Array.isArray(subcat.items)) count += subcat.items.length; }); }); });
    }

    return count;
};

OEFNStartSelectionContentFactory.build = function(content) {
    let content_ = this.normalizeContent(content);

    if(OEFNStartSelectionContentFactory.areCategories(content_))    {
        let module = {
                type: OEFNStartSelectionItemTypes.module,
                isHidden: true,
                categories: Array.isArray(content_) ? content_ : []
        };

        // if content is a single category, copy all custom params to the created module, this might be helpful, e.g., if we want to forward a module id to which the category belongs for callbacks
        if(typeof(content) === 'object')    {
            let {label, subcategories, ...rest} = content;
            module = Object.assign(rest, module);
        }

        return [module];
    }

    return content_;
};

OEFNStartSelectionContentFactory.fromTargetMenuDef = function(targetMenuDef)    {
    let items = [];

    let modules = [
        {
            type: OEFNStartSelectionItemTypes.module,
            isHidden: true,
            categories: [{
                subcategories: [{
                    items: items
                }]
            }]
        }
    ];

    let entries = targetMenuDef || [];
    let prefix;
    if(!Array.isArray(targetMenuDef))    {
        prefix = targetMenuDef.prefix;
        entries = targetMenuDef.parts || [];
    }

    if(!entries) return modules;

    entries.forEach(entry => {
        if(!entry.target)   return;
        const labelIsObject = typeof(entry.label) === 'object';
        const id = labelIsObject && entry.label.id ? entry.label.id : ('target_menu_' + (prefix ? (prefix + '_') : '') + entry.target);
        const labelText = labelIsObject ? entry.label.text : entry.label;
        items.push({target: entry.target, label: {text: labelText, id: id}});
    });

    return modules;
};

//
export class OEFNStartSelectionModel  {

    constructor(content) {
        this.modules = OEFNStartSelectionContentFactory.build(content);
    }

    areModulesHidden()  {
        return OEFNStartSelectionContentFactory.areModulesHidden(this.modules);
    }

    forEachItem(items, fn, path)  {
        if(typeof(fn) !== 'function' || !Array.isArray(items))   return;

        for(let i = 0; i < items.length; ++i)   {
            let item = items[i];
            if(!item)   continue;

            let ret = fn(item, OEFNStartSelectionItemTypes.item, Object.assign({item: item.proto ? item.proto : item, itemIndex: i}, path));
            if(ret !== undefined)    return ret;
        }
    }

    forEachSubcategory(subcategories, fn, path)  {
        if(typeof(fn) !== 'function' || !Array.isArray(subcategories))   return;

        for(let i = 0; i < subcategories.length; ++i)  {
            let subcategory = subcategories[i];
            if(!subcategory)   continue;
            
            let path_ = Object.assign({subcategory: subcategory.proto ? subcategory.proto : subcategory, subcategoryIndex: i}, path);

            let ret = fn(subcategory, OEFNStartSelectionItemTypes.subcategory, path_);
            if(ret !== undefined)    return ret;

            ret = this.forEachItem(subcategory.items, fn, path_);
            if(ret !== undefined)    return ret;
        }
    }

    forEachCategory(categories, fn, path)  {
        if(typeof(fn) !== 'function' || !Array.isArray(categories))   return;

        for(let i = 0; i < categories.length; ++i) {
            let category = categories[i];
            if(!category)   continue;

            let path_ = Object.assign({category: category.proto ? category.proto : category, categoryIndex: i}, path);

            let ret = fn(category, OEFNStartSelectionItemTypes.category, path_);
            if(ret !== undefined)    return ret;
            
            ret = this.forEachSubcategory(category.subcategories, fn, path_);
            if(ret !== undefined)    return ret;
        }
    }

    forEachModule(modules, fn)  {
        if(typeof(fn) !== 'function' || !Array.isArray(modules))   return;

        for(let i = 0; i < modules.length; ++i) {
            let module = modules[i];
            if(!module)   continue;

            let path = {module: module.proto ? module.proto : module, moduleIndex: i};

            let ret = fn(module, OEFNStartSelectionItemTypes.module, path);
            if(ret !== undefined)    return ret;

            ret = this.forEachCategory(module.categories, fn, path);
            if(ret !== undefined)    return ret;
        }
    }

    forEach(fn) {
        return this.forEachModule(this.modules, fn);
    }

    moduleForIndex(iM)  {
        return this.modules[iM];
    }

    categoryForIndex(iM, iC)  {
        let module = this.moduleForIndex(iM);
        if(!module || !Array.isArray(module.categories) || iC >= module.categories.length)  return;
        return module.categories[iC];
    }

    getSelectedModuleIndex(target)   {
        let sel = this.modules.indexOf(this.itemForItem(this.itemForTarget(target), OEFNStartSelectionItemTypes.module));
        return sel >= 0 ? sel : (this.modules.length ? 0 : -1);
    }

    getCurrentModuleIndex(target)   {
        return this.getSelectedModuleIndex(target);
    }

    getSelectedCategoryIndex(iM, target)   {
        let module = this.moduleForIndex(iM);
        if(!module || !Array.isArray(module.categories) || !module.categories.length)   return -1;

        let categoryIndex;
        categoryIndex = this.forEachCategory(module.categories, (item, type, path) =>   {
            if(type === OEFNStartSelectionItemTypes.category)   categoryIndex = path.categoryIndex;
            if(type === OEFNStartSelectionItemTypes.item && item.target === target)   return categoryIndex;
        });

        return categoryIndex !== undefined ? categoryIndex : 0;
    }

    createModulesState(target, moduleLicensingAccess, itemLicensingAccess)    {
        let modules = this.modules.map((module, moduleIndex) => {
            const {categories, ...moduleProto} = module;
            let disabled = !!module.disabled;
            if(!disabled && moduleLicensingAccess)   disabled = !moduleLicensingAccess(moduleProto);

            let categories_ = !Array.isArray(categories) ? categories : categories.map((category, categoryIndex) => {
                const {subcategories, ...categoryProto} = category;

                let subcategories_ = !Array.isArray(subcategories) ? subcategories : subcategories.map((subcategory, subcategoryIndex) => {
                    const {items, ...subcategoryProto} = subcategory;
    
                    let items_ = !Array.isArray(items) ? items : items.map((item, itemIndex) => {
                        let disabled = !!item.disabled;
                        if(!disabled && itemLicensingAccess)   disabled = !itemLicensingAccess(item, {module: moduleProto, moduleIndex: moduleIndex, category: categoryProto, categoryIndex: categoryIndex, subcategory: subcategoryProto, subcategoryIndex: subcategoryIndex, item: item, itemIndex: itemIndex});

                        return {
                            ...item,
                            disabled: disabled,
                            proto: item
                        };
                    });
                    
                    return {
                        ...subcategoryProto,
                        items: items_
                    };
                });

                return {
                    ...categoryProto,
                    subcategories: subcategories_
                };
            });

            return {
                ...moduleProto,
                disabled: disabled,
                selectedCategory: typeof(moduleProto.selectedCategory) === 'number' ? moduleProto.selectedCategory : this.getSelectedCategoryIndex(moduleIndex, target),
                categories: categories_,
                proto: moduleProto
            };
        });

        return modules;
    }

    updateSelectedCategoryState(modules, target)  {
        if(!Array.isArray(modules))  return;
        return modules.map((module, i) => {
            return {
                ...module,
                selectedCategory: typeof(module.proto.selectedCategory) === 'number' ? module.proto.selectedCategory : this.getSelectedCategoryIndex(i, target),
            };
        });
    }

    updateDisabledState(modules, moduleLicensingAccess, itemLicensingAccess)  {
        let ret = clone(modules);
        this.forEachModule(ret, (data, type, path) => {
            if(type === OEFNStartSelectionItemTypes.module) {
                data.disabled = !!data.proto.disabled;
                if(!data.disabled && moduleLicensingAccess)   data.disabled = !moduleLicensingAccess(data.proto);
            } else if(type === OEFNStartSelectionItemTypes.item) {
                data.disabled = !!data.proto.disabled;
                if(!data.disabled && itemLicensingAccess)   data.disabled = !itemLicensingAccess(data.proto, path);
            }
        });
        return ret;
    }

    itemForTarget(target)   {
        if(typeof(target) !== 'string')  return;
        return this.forEach((data, type) => {
            if(type === OEFNStartSelectionItemTypes.item && data.target === target)   return data;
        });
    }

    itemForItem(item, retType, queryType = OEFNStartSelectionItemTypes.item)   {
        if(!item)   return;
        let lastCandidate;
        return this.forEach((data, dataType) => {
            if(dataType === retType) lastCandidate = data;
            if(dataType === queryType && data === item)    return lastCandidate; 
        });
    }

    localizedStrings(si)  {
        let ret = {};

        this.forEach((data, type) => {
            if(!data.label || !data.label.id || ret[data.label.id])   return;
            ret[data.label.id] = si ? si.getLocalizedStringEnc(data.label.id) : '';
        });

        return ret;
    }
}

OEFNStartSelectionModel.itemCount = function(model)   {
    return OEFNStartSelectionContentFactory.itemCount(typeof(model) === 'object' && !Array.isArray(model) ? (model.content ? model.content : model.modules) : model);
}

OEFNStartSelectionModel.unpackLabel = function(label, strings)   {
    if(!label)  return null;
    return label.id && strings ? strings[label.id] : label.text;
}

//
export const OEFNStartSelectionPropTypes = {
    label: PropTypes.shape({
        text: PropTypes.string,
        id: PropTypes.string,
    })
};

OEFNStartSelectionPropTypes.item = PropTypes.shape({
    label: OEFNStartSelectionPropTypes.label,
    disabled: PropTypes.bool
});

OEFNStartSelectionPropTypes.items = PropTypes.arrayOf(OEFNStartSelectionPropTypes.item);

OEFNStartSelectionPropTypes.subcategory = PropTypes.shape({
    label: OEFNStartSelectionPropTypes.label,
    items: OEFNStartSelectionPropTypes.items
});

OEFNStartSelectionPropTypes.subcategories = PropTypes.arrayOf(OEFNStartSelectionPropTypes.subcategory);

OEFNStartSelectionPropTypes.category = PropTypes.shape({
    label: OEFNStartSelectionPropTypes.label,
    subcategories: OEFNStartSelectionPropTypes.subcategories
});

OEFNStartSelectionPropTypes.categories = PropTypes.arrayOf(OEFNStartSelectionPropTypes.category);

OEFNStartSelectionPropTypes.module = PropTypes.shape({
    label: OEFNStartSelectionPropTypes.label,
    icon: PropTypes.string,
    disabled: PropTypes.bool,
    isHidden: PropTypes.bool,
    categories: OEFNStartSelectionPropTypes.categories
});

OEFNStartSelectionPropTypes.modules = PropTypes.arrayOf(OEFNStartSelectionPropTypes.module);

OEFNStartSelectionPropTypes.content = PropTypes.oneOfType([
    OEFNStartSelectionPropTypes.module,
    OEFNStartSelectionPropTypes.category,
    OEFNStartSelectionPropTypes.modules,
    OEFNStartSelectionPropTypes.categories
]);

OEFNStartSelectionPropTypes.decl = {
    moduleId: PropTypes.string,
    target: PropTypes.string,
    locStringPrefix: PropTypes.string,
    disabled: PropTypes.bool,
    moduleBarDisabled: PropTypes.bool,
    categoryBarDisabled: PropTypes.bool,
    itemsDisabled: PropTypes.bool,
    content: OEFNStartSelectionPropTypes.content,
    currentItemInactive: PropTypes.bool,
    currentItemLabel: PropTypes.bool,
    categoryLabel: PropTypes.bool,
    onItemClick: PropTypes.func,
    licensingAccess: PropTypes.func,
    moduleLicensingAccess: PropTypes.func,
    itemLicensingAccess: PropTypes.func
};

OEFNStartSelectionPropTypes.startSelection = PropTypes.shape(OEFNStartSelectionPropTypes.decl);
